import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { LayoutList } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Alert } from '../ui/Alert';
import { apiFetch } from '../../lib/api';
import { ApiError, type Template } from '../../types/api';
import { CopyImagesPanel } from './CopyImagesPanel';
import { HtmlCheckReport } from './HtmlCheckReport';
import { HtmlPreview } from './HtmlPreview';
import { HtmlSourceEditor } from './HtmlSourceEditor';
import { useHtmlCheck } from './useHtmlCheck';
import { useRehostImages } from './useRehostImages';
import { SectionEditsNotice } from '../section-editor/SectionEditsNotice';

/**
 * Editor for an HTML template: the HTML source with the server's check
 * below it on the left, and a live preview on the right. It never builds blocks and never
 * sends a `blockJson`, so saving cannot turn the template into a block one
 * (the server also refuses that). The stored HTML is the single source of
 * truth: this component only holds the text being edited and writes it back.
 */
export function HtmlTemplateEditor({
  template,
  readOnly,
}: {
  template: Template;
  readOnly: boolean;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(template.name);
  const [html, setHtml] = useState(template.html);

  // Reload only when a different template (or a saved version) arrives.
  useEffect(() => {
    setName(template.name);
    setHtml(template.html);
  }, [template.id, template.updatedAt, template.name, template.html]);

  const onRehosted = useCallback((next: string) => setHtml(next), []);
  const rehost = useRehostImages(html, onRehosted);
  const copying = rehost.run.status === 'running';

  const check = useHtmlCheck(html);
  const report = check.report;
  const dirty = name !== template.name || html !== template.html;

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ template: Template }>(`/templates/${template.id}`, {
        method: 'PUT',
        body: { name, html },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['template', template.id] });
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  const saveError =
    saveMutation.error instanceof ApiError && saveMutation.error.body.message
      ? saveMutation.error.body.message
      : saveMutation.isError
        ? 'The template could not be saved. Check the problems below the HTML.'
        : null;
  const canSave = !readOnly && dirty && !check.pending && !copying && report?.valid === true;

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="flex w-full max-w-[420px] flex-col gap-1.5">
          <Label htmlFor="t-name">Name</Label>
          <Input
            id="t-name"
            value={name}
            disabled={readOnly}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {saveMutation.isSuccess && !dirty ? (
            <span className="text-sm text-success-text" role="status">
              Saved.
            </span>
          ) : null}
          {dirty && !readOnly ? <span className="text-sm text-muted">Unsaved changes</span> : null}
          {dirty ? (
            <Button variant="secondary" disabled title="Save your changes first.">
              <LayoutList size={16} aria-hidden="true" />
              Edit sections
            </Button>
          ) : (
            <Button variant="secondary" asChild>
              <Link to={`/skabeloner/${template.id}/sections`} className="no-underline">
                <LayoutList size={16} aria-hidden="true" />
                Edit sections
              </Link>
            </Button>
          )}
          <Button
            disabled={!canSave || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save template'}
          </Button>
        </div>
      </div>
      <p className="m-0 max-w-[80ch] text-sm text-muted">
        {dirty ? (
          <>
            <span>Save your changes first.</span> Then Edit sections lets you change texts, images
            and links without touching the HTML.
          </>
        ) : (
          'Edit sections lets you change texts, images and links, and move sections, without touching the HTML.'
        )}{' '}
        Campaigns made from this template keep their own copy; automations send the latest saved
        version.
      </p>
      {template.sections && html !== template.html ? <SectionEditsNotice /> : null}
      {saveError ? <Alert>{saveError}</Alert> : null}

      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-4">
          <HtmlSourceEditor
            id="t-html"
            value={html}
            onChange={setHtml}
            disabled={readOnly || copying}
          />
          {report ? (
            <HtmlCheckReport
              report={report}
              pending={check.pending}
              imageAction={
                readOnly ? null : (
                  <CopyImagesPanel
                    images={report.images}
                    run={rehost.run}
                    onCopy={() => void rehost.start()}
                    disabled={check.pending}
                  />
                )
              }
            />
          ) : null}
          {check.isError ? <Alert>The HTML could not be checked. Try again shortly.</Alert> : null}
        </div>
        <div className="flex min-w-0 flex-col gap-1.5 md:sticky md:top-4">
          <span className="text-sm font-medium text-text">Preview</span>
          <HtmlPreview html={report?.previewHtml ?? null} className="h-[560px]" />
        </div>
      </div>
    </>
  );
}
